import { ErrorHandler, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ENV } from '@core/env.token';
import { NotificationService } from '@core/notification-service/notification.service';
import {
  ContextualError,
  ForbiddenError,
  NotFoundError,
  DuplicateEmailError,
  InactiveUserError,
  PortalAccessDeniedError,
  RoutingError,
  SessionExpiredError,
  UnknownError,
} from './errors';
import { AppTranslationService } from '@core/translation.service';
import { LogService } from '@core/log.service';
import { HttpErrorResponse } from '@angular/common/http';

export class GlobalErrorHandler implements ErrorHandler {
  private readonly router = inject(Router);
  private readonly env = inject(ENV);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(AppTranslationService);
  private readonly logger = inject(LogService);

  private readonly accessDeniedRoute = '/access-denied';

  private wrapHttpError(error: HttpErrorResponse) {
    if (error.status === 403) {
      return new ForbiddenError().setContext({ originalError: error });
    }

    if (error.status === 404) {
      return new NotFoundError().setContext({ originalError: error });
    }

    return new UnknownError().setContext({ originalError: error });
  }

  handleError(error: unknown): void {
    if (error == null) {
      // Somehow handling a nullish error, log as unknown
      return this.handleError(new UnknownError());
    }

    if (error instanceof HttpErrorResponse) {
      // Wrap common HTTP errors into more specific error types
      return this.handleError(this.wrapHttpError(error));
    }

    if (!(error instanceof Error)) {
      // Not handling an actual error. Still handle it as unknown, but attach a bit more context
      return this.handleError(new UnknownError().setContext({ originalError: error }));
    }

    this.logger.error(error);

    if (error instanceof DuplicateEmailError) {
      this.router.navigate([this.accessDeniedRoute], {
        queryParams: { reason: 'duplicate-email' },
      });

      return;
    }

    if (error instanceof InactiveUserError) {
      this.router.navigate([this.accessDeniedRoute], {
        queryParams: { reason: 'inactive-user' },
      });

      return;
    }

    if (error instanceof PortalAccessDeniedError) {
      this.router.navigate([this.accessDeniedRoute]);

      return;
    }

    if (error instanceof SessionExpiredError && error.getContext()?.loginPath) {
      const loginPath = error.getContext()?.loginPath as string;
      this.router.navigate([loginPath]);

      return;
    }

    if (error instanceof RoutingError) {
      // Routing errors have predefined error codes.
      // Unliike redirection scenarios, we don't need to include the error as extra info as it's already been logged
      this.router.navigate([`/error/${error.errorCode}`]);

      return;
    }

    // Outside of production, we can safely just always display the error to devs
    if (!this.env.production) {
      this.notificationService.error(error.message);
    }

    if (error instanceof ContextualError) {
      // In production, we probably don't want to display technical errors as visibly, so only if the error has an appropriate context do we display it
      const errorContext = error.getContext();
      if (errorContext && 'userMessage' in errorContext) {
        const userMessage = (errorContext as { userMessage: string }).userMessage;
        this.notificationService.error(this.translateService.translate(userMessage));
      }
    }
    // Also redirect the user to the generic error page
    this.router.navigate([`/error/500`]);
  }
}
